import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State
} from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';

import { InputType } from '../uui-input/uui-input';

@Component({
  tag: 'uui-search-input',
  styleUrl: 'uui-search-input.css',
  shadow: false
})
export class UUIInputSearch {
  @Prop() inputID: string;
  @Prop() width: string = '100%';
  @Prop({ mutable: true, reflect: true }) value = '';
  @Prop() error: boolean = false;
  @Prop() disabled: boolean = false;
  @Prop() name: string = '';
  @Prop() type: InputType;
  /** display a spinner when data is being fetched */
  @Prop() fetching: boolean = false;
  @Prop() placeholder: string = '';
  @Prop() inverse: boolean = false;
  @Prop() list: string;
  @Prop() pattern: string;
  @Prop() autocomplete?: string;
  @Prop() autofocus: boolean = false;
  @Prop() iconTabStop = false;
  @Prop() searchButtonPosition: 'start' | 'end' | 'none' = 'start';
  @Prop() clearButtonPosition: 'start' | 'end' | 'none' = 'end';
  /// A proposed completion of the search text.
  @Prop({ attribute: 'proposedvalue', mutable: true }) proposedValue: string;
  /** this event fires when the clear text button is clicked */
  @Event({ bubbles: true, composed: true }) cleartext: EventEmitter;
  /** passes the value of the search input */
  @Event({ bubbles: true, composed: true }) textchange: EventEmitter;
  @Event({ eventName: 'inputBlur', bubbles: true, composed: true })
  inputBlur: EventEmitter;
  @Event({ eventName: 'inputFocus', bubbles: true, composed: true })
  inputFocus: EventEmitter;
  @Element() hostElement: HTMLStencilElement;
  @State() hasFocus: boolean = false;

  @Method()
  async clear() {
    this.value = '';
    this.proposedValue = '';
    const uuiInput: any = this.hostElement?.querySelector('uui-input');
    uuiInput?.clear();
    this.cleartext.emit();
  }

  @Method()
  async clearText(e?: PointerEvent | MouseEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.clear();
    this.setFocus();
  }

  @Method()
  async setFocus() {
    const els = this.hostElement?.getElementsByTagName('input');

    if (els?.length) {
      const input = els[0];
      input.focus();
    }
  }

  handleValueChange(e) {
    if (!e?.detail) {
      return;
    }
    this.value = e?.detail;
    if (!this.value) {
      this.clearText(e);
    }
  }

  handleTextChange(e) {
    this.value = e.target.value;
    this.textchange.emit(this.value);
  }

  render() {
    let searchButtonSlot = 'input-icon-start';
    let clearButtonSlot = 'input-icon-end';
    let showSearchButton = true;
    let showClearButton = this.clearButtonPosition !== 'none';
    if (this.searchButtonPosition === 'end') {
      searchButtonSlot = 'input-icon-end';
    }
    if (this.clearButtonPosition === 'start') {
      clearButtonSlot = 'input-icon-start';
    }
    if (this.searchButtonPosition === 'none') {
      showSearchButton = false;
    }
    return (
      <uui-input
        inputID={this.inputID}
        autocomplete={this.autocomplete}
        type={this.type}
        pattern={this.pattern}
        list={this.hasFocus ? this.list : undefined}
        value={this.value}
        name={this.name}
        proposedValue={this.proposedValue}
        placeholder={this.placeholder}
        disabled={this.disabled}
        autofocus={this.autofocus}
        onInput={(e: UIEvent) => this.handleValueChange(e)}
        onChange={(e: UIEvent) => this.handleValueChange(e)}
        onTextChange={e => this.handleTextChange(e)}
        onInputBlur={() => {
          this.hasFocus = false;
          this.inputBlur.emit();
        }}
        onInputFocus={() => {
          this.hasFocus = true;
          this.inputFocus.emit();
        }}
      >
        <slot />
        {showClearButton || this.fetching ? (
          <button
            title="clear"
            slot={clearButtonSlot}
            onMouseDown={e => {
              if(this.fetching) return;
              this.clearText(e);
            }}
            onClick={e => {
              e?.preventDefault();
              e?.stopPropagation();
            }}
            class={`uui-input-search-button ${this.hasFocus || this.fetching ? '' : 'hide'}`}
            tabIndex={this.iconTabStop ? 0 : -1}
          >
            {this.fetching ? <uui-spinner variant='small'></uui-spinner> : <svg
              height="16px"
              width="16px"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
            >
              <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z" />
            </svg>}
          </button>
        ) : null}
        {!this.hasFocus && showSearchButton ? (
          <button
            title="search"
            slot={searchButtonSlot}
            class="uui-input-search-button"
            tabIndex={-1}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="16px"
              width="16px"
              viewBox="0 0 512 512"
            >
              <path d="M508.5 481.6l-129-129c-2.3-2.3-5.3-3.5-8.5-3.5h-10.3C395 312 416 262.5 416 208 416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c54.5 0 104-21 141.1-55.2V371c0 3.2 1.3 6.2 3.5 8.5l129 129c4.7 4.7 12.3 4.7 17 0l9.9-9.9c4.7-4.7 4.7-12.3 0-17zM208 384c-97.3 0-176-78.7-176-176S110.7 32 208 32s176 78.7 176 176-78.7 176-176 176z" />
            </svg>
          </button>
        ) : null}
      </uui-input>
    );
  }
}
